import {
  ArrowDropDown,
  Brightness2,
  Brightness7,
  HelpOutlineOutlined,
  LaunchOutlined,
  MailOutlined,
} from '@mui/icons-material';
import Link from '@Common/components/Link';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
  Theme,
  List,
  Drawer,
  useTheme,
  Box,
  Badge,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { useRouter } from 'next/router';
import PremiumIcon from '@Common/quillElements/icon/PremiumIcon';
import { extensionLinks } from 'config/constants';
import Track from 'src/authentication/handlers/track';
import { getQueryParams } from '@Common/handlers/helper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import getConfig from 'next/config';
import { setRegionalLanguage, setSidebarPinnned } from '@Common/actions/commonActions';
import QuillLocalStorage from 'core/utils/localStorageClient';
import CommonTrack from '@Common/handlers/track';
import { FC, useEffect, useRef, useState } from 'react';
import { LinkProps } from 'next/link';
import LanguageMenu from './header/LanguageMenu';
import BadgePrimary from './BadgePrimary';

interface SidebarProps {
  sidebarHoverOpen: boolean;
  setSidebarHoverOpen: Function;
  handleThemeChange: Function;
  LANGUAGES: any;
  isBannerShown: boolean;
  bannerHeight: any;
}
interface StyleProps {
  isBannerShown: boolean;
  bannerHeight: any;
  sidebarHovered: boolean;
  sidebarPinned: boolean;
  isCoWriter: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  sidebar: {
    position: 'absolute',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiListItemButton-root': {
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: -41,
      position: 'relative',
      zIndex: 1200,
    },
  },
  sidebarPaper: {
    position: 'sticky',
    top: '49px',
    height: 'calc(100vh - 53px)',
    borderRight: `1px solid ${theme.palette.divider}`,
    justifyContent: 'space-between',
  },
  desktopSidebar: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  sidebarHovered: {
    transition: 'width 0.2s ease-in-out 500ms',
    '& .MuiDrawer-paper': {
      transition: 'width 0.2s ease-in-out 500ms',
    },
  },
  mobileSidebar: {
    top: (props) => (props.isBannerShown ? props.bannerHeight + 50 : 50),
    zIndex: 1202,
    '& .MuiBackdrop-root': {
      position: 'absolute',
    },
    '& .MuiDrawer-paper': {
      width: 220,
      marginTop: (props) => (props.isBannerShown ? props.bannerHeight + 49 : 49),
      maxHeight: (props) =>
        props.isBannerShown ? `calc(100vh - ${props.bannerHeight + 45}px )` : 'calc(100vh - 45px)',
      justifyContent: 'space-between',
    },
    '& .MuiListItemButton-root': {
      padding: 0,
    },
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  extensionSidebar: {
    top: '40px !important',
    '& .MuiDrawer-paper': {
      marginTop: '40px !important',
      maxHeight: 'calc(100vh - 40px) !important',
    },
  },
  fillIcon: {
    fill: theme.specialColors.ICON_DEFAULT_COLOR,
  },
  strokeIcon: {
    stroke: theme.specialColors.ICON_DEFAULT_COLOR,
  },
  productIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
  },
  productLink: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
    padding: '8px 13px',
    width: '100%',
    maxHeight: 40,
    [theme.breakpoints.up(1600)]: {
      padding: '8px 15px',
    },
    '& .MuiListItemIcon-root': {
      width: 24,
      height: 24,
      marginRight: 8,
      minWidth: 24,
      [theme.breakpoints.up(1600)]: {
        width: 28,
        height: 28,
      },
      '& img': {
        width: 24,
        height: 24,
        [theme.breakpoints.up(1600)]: {
          width: 28,
          height: 28,
        },
      },
    },
    '& .MuiListItemText-primary': {
      opacity: (props) =>
        props.sidebarHovered || (props.sidebarPinned && !props.isCoWriter) ? 1 : 0,
      transition: (props) =>
        props.sidebarHovered ? 'opacity 0s ease-in 0.5s' : 'opacity 0.1s ease-out',
      lineHeight: '16px',
      fontSize: 13,
      fontWeight: 600,
      [theme.breakpoints.up(1600)]: {
        fontSize: 14,
      },
    },
    '& .MuiSvgIcon-root': {
      [theme.breakpoints.up(1600)]: {
        width: 28,
        height: 28,
      },
    },
    '& .MuiSvgIcon-root[data-testid="LaunchOutlinedIcon"]': {
      width: 16,
      height: 16,
      [theme.breakpoints.up(1600)]: {
        width: 18,
        height: 18,
      },
    },
  },
  active: {
    position: 'absolute',
    height: 24,
    left: 0,
    borderRadius: '0px 4px 4px 0px',
    borderLeft: '4px solid',
    [theme.breakpoints.up(1600)]: {
      height: 28,
      borderLeft: '6px solid',
    },
  },
  appVersionBar: {
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    padding: '12px 15px',
    [theme.breakpoints.up(1600)]: {
      fontSize: 14,
    },
  },
  hideListItem: {
    [theme.breakpoints.down(800)]: {
      display: 'none',
    },
  },
  betaBadge: {
    opacity: (props) =>
      props.sidebarHovered || (props.sidebarPinned && !props.isCoWriter) ? 0 : 1,
    transition: (props) =>
      props.sidebarHovered ? 'opacity 0.1s ease-out 0.5s' : 'opacity 0.1s ease-in',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    width: 8,
    height: 8,
    borderRadius: 6,
    top: -6,
    right: -7,
  },
  boldText: {
    '& .MuiListItemText-primary': {
      fontWeight: 700,
    },
  },
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: 210,
  [theme.breakpoints.up(1600)]: {
    width: 230,
  },
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    width: 210,
    transition: 'width 0.2s ease-in-out',
    overflowX: 'hidden',
    overflowY: 'auto',
    '& .MuiDrawer-paper': {
      width: 210,
      transition: 'width 0.2s ease-in-out',
      overflowX: 'hidden',
      overflowY: 'auto',
      [theme.breakpoints.up(1600)]: {
        width: 230,
      },
      [theme.breakpoints.down(1080)]: {
        boxShadow: '0px 1.5px 12px rgba(0, 0, 0, 0.2)',
      },
    },
  }),
  ...(!open && {
    transition: 'width 0.2s ease-in-out',
    overflow: 'hidden',
    width: 50,
    [theme.breakpoints.up(1600)]: {
      width: 58,
    },
    [theme.breakpoints.down(599)]: {
      width: 0,
    },
    '& .MuiDrawer-paper': {
      transition: 'width 0.2s ease-in-out',
      overflow: 'hidden',
      width: 50,
      [theme.breakpoints.up(1600)]: {
        width: 58,
      },
      [theme.breakpoints.down(599)]: {
        width: 0,
      },
    },
    '& .MuiBox-root': {
      display: 'none',
    },
  }),
}));

const { publicRuntimeConfig } = getConfig();

const SidebarLink: FC<LinkProps> = ({ href, children }) => (
  <Link href={href} inlineStyles={{ flex: 1 }}>
    {children}
  </Link>
);

const Sidebar = (props: SidebarProps) => {
  const isMobile = useSelector((rootState: RootState) => rootState.common.platform.isMobile);
  const isUserPremium = useSelector((state: RootState) => state.user?.premium);
  const regionalLanguage = useSelector(
    (rootState: RootState) => rootState?.common.regionalLanguage
  );
  const isExtension = useSelector((rootState: RootState) => rootState.common.platform.isExtension);
  const native = useSelector((rootState: RootState) => rootState.common.platform.native);
  const router = useRouter();
  const theme = useTheme();
  const dispatch = useDispatch();
  const pageUrl = router.pathname;
  const appVersion = `${publicRuntimeConfig.APP_VERSION}`;

  const [languageMenuOpen, setLanguageMenuOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const sidebarPinned = useSelector((rootState: RootState) => rootState.common.sidebarPinned);
  const isSubscriptionPaused = useSelector((state: RootState) => state.user?.isSubPaused);
  const isCoWriter = router.pathname.includes('/co-writer');
  const classes = useStyles({
    isBannerShown: props.isBannerShown,
    bannerHeight: props.bannerHeight,
    sidebarHovered: props.sidebarHoverOpen,
    sidebarPinned,
    isCoWriter,
  });

  useEffect(() => {
    if (
      QuillLocalStorage.getInstance().get('sideBarOnboarding', 'false') === 'false' &&
      !isCoWriter &&
      window.innerWidth > 1365
    ) {
      dispatch(setSidebarPinnned(true));
      QuillLocalStorage.getInstance().set('sideBarOnboarding', 'true');
    }
  }, []);

  const extensionClickHandler = (value: 'chrome' | 'docs' | 'word') => {
    Track.extensionLinkClicked(value, 'side-navbar');

    if (value === 'chrome') {
      window.open(`${extensionLinks.chrome}${getQueryParams('sideNavbar')}`, '_blank');
    } else window.open(extensionLinks[value], '_blank');
  };

  const handleLanguageChange = (language: string) => {
    dispatch(setRegionalLanguage(language));
    setLanguageMenuOpen(false);
    QuillLocalStorage.getInstance().set('REGION', language);
    CommonTrack.settingChanged({ REGION: language });
  };

  const handlePremiumClick = () => {
    if (isSubscriptionPaused) {
      window.open(`/settings${native ? `?native=${native}` : ''}`);
    } else {
      window.open(`/premium?referrer=side_navbar${native ? `&native=${native}` : ''}`);
    }
  };

  const drawer = (
    <>
      <div>
        <List>
          <ListItemButton>
            <SidebarLink href="/">
              <a className={`${classes.productLink}`}>
                <div
                  className={`${pageUrl === '/' ? classes.active : ''}`}
                  style={{ borderColor: theme.palette.primary.main }}
                />
                <ListItemIcon
                  className={classes.productIcon}
                  style={{ backgroundColor: theme.palette.primary.main }}
                >
                  <img
                    style={{ width: 14, height: 14 }}
                    src="/images/theme/commonImages/paraphraser-new.svg"
                    alt="paraphraser icon"
                  />
                </ListItemIcon>
                <ListItemText className={`${pageUrl === '/' ? classes.boldText : ''}`}>
                  Paraphraser
                </ListItemText>
              </a>
            </SidebarLink>
          </ListItemButton>
          <ListItemButton>
            <SidebarLink href="/grammar-check">
              <a className={`${classes.productLink}`}>
                <div
                  className={`${pageUrl === '/grammar-check' ? classes.active : ''}`}
                  style={{ borderColor: '#EF414C' }}
                />
                <ListItemIcon
                  className={classes.productIcon}
                  style={{ backgroundColor: '#EF414C' }}
                >
                  <img
                    style={{ width: 16, height: 16 }}
                    src="/images/theme/commonImages/grammar-checker-new.svg"
                    alt="grammar checker icon"
                  />
                </ListItemIcon>
                <ListItemText className={`${pageUrl === '/grammar-check' ? classes.boldText : ''}`}>
                  Grammar Checker
                </ListItemText>
              </a>
            </SidebarLink>
          </ListItemButton>
          {!isExtension && (
            <ListItemButton>
              <SidebarLink href="/plagiarism-checker">
                <a className={`${classes.productLink}`}>
                  <div
                    className={`${pageUrl === '/plagiarism-checker' ? classes.active : ''}`}
                    style={{ borderColor: '#F27C0E' }}
                  />
                  <ListItemIcon
                    className={classes.productIcon}
                    style={{ backgroundColor: '#F27C0E' }}
                  >
                    <Badge
                      variant="dot"
                      color="primary"
                      classes={{
                        badge: classes.betaBadge,
                      }}
                    >
                      <img
                        style={{ width: 14, height: 14 }}
                        src="/images/theme/commonImages/plag-checker-new.svg"
                        alt="plagiarism checker icon"
                      />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    className={`${pageUrl === '/plagiarism-checker' ? classes.boldText : ''}`}
                  >
                    Plagiarism Checker
                    <BadgePrimary label="New" />
                  </ListItemText>
                </a>
              </SidebarLink>
            </ListItemButton>
          )}
          {!isMobile && !isExtension && !native && (
            <ListItemButton className={classes.hideListItem}>
              <SidebarLink href="/co-writer">
                <a className={`${classes.productLink}`}>
                  <div
                    className={`${pageUrl.includes('/co-writer') ? classes.active : ''}`}
                    style={{ borderColor: '#319DFF' }}
                  />
                  <ListItemIcon
                    className={classes.productIcon}
                    style={{ backgroundColor: '#319DFF' }}
                  >
                    <img
                      style={{ width: 15, height: 15 }}
                      src="/images/theme/commonImages/co-writer-new.svg"
                      alt="co-writer icon"
                    />
                  </ListItemIcon>
                  <ListItemText
                    className={`${pageUrl.includes('/co-writer') ? classes.boldText : ''}`}
                  >
                    Co-Writer
                  </ListItemText>
                </a>
              </SidebarLink>
            </ListItemButton>
          )}
          <ListItemButton>
            <SidebarLink href="/summarize">
              <a className={`${classes.productLink}`}>
                <div
                  className={`${pageUrl === '/summarize' ? classes.active : ''}`}
                  style={{ borderColor: '#8051FF' }}
                />
                <ListItemIcon
                  className={classes.productIcon}
                  style={{ backgroundColor: '#8051FF' }}
                >
                  <img
                    style={{ width: 15, height: 15 }}
                    src="/images/theme/commonImages/summarizer-new.svg"
                    alt="summarizer icon"
                  />
                </ListItemIcon>
                <ListItemText className={`${pageUrl === '/summarize' ? classes.boldText : ''}`}>
                  Summarizer
                </ListItemText>
              </a>
            </SidebarLink>
          </ListItemButton>
          {!isExtension && !native && (
            <ListItemButton>
              <SidebarLink href="/citation-generator">
                <a className={`${classes.productLink}`}>
                  <div
                    className={`${pageUrl === '/citation-generator' ? classes.active : ''}`}
                    style={{ borderColor: '#0067C5' }}
                  />
                  <ListItemIcon
                    className={classes.productIcon}
                    style={{ backgroundColor: '#0067C5' }}
                  >
                    <img
                      style={{ width: 15, height: 15 }}
                      src="/images/theme/commonImages/citation-generator-new.svg"
                      alt="citation generator icon"
                    />
                  </ListItemIcon>
                  <ListItemText
                    className={`${pageUrl === '/citation-generator' ? classes.boldText : ''}`}
                  >
                    Citation Generator
                  </ListItemText>
                </a>
              </SidebarLink>
            </ListItemButton>
          )}
        </List>
        <Divider />
        {!isUserPremium && !native && (
          <>
            <List style={{ padding: 0 }}>
              <ListItemButton style={{ padding: '4px 0' }} onClick={handlePremiumClick}>
                <a className={classes.productLink} target={isExtension ? '_blank' : ''}>
                  <div
                    className={`${pageUrl === '/premium' ? classes.active : ''}`}
                    style={{ borderColor: theme.palette.secondary.main }}
                  />
                  <ListItemIcon>
                    <PremiumIcon />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: `${theme.palette.secondary.main} !important`,
                      },
                    }}
                  >
                    QuillBot Premium
                  </ListItemText>
                </a>
              </ListItemButton>
            </List>
            <Divider />
          </>
        )}
        {!isMobile && !isExtension && !native && (
          <>
            <List>
              <ListItemButton onClick={() => extensionClickHandler('chrome')}>
                <a className={classes.productLink}>
                  <ListItemIcon>
                    <img src="/images/theme/commonImages/chrome.png" alt="chrome extension" />
                  </ListItemIcon>
                  <ListItemText sx={{ marginRight: '8px' }}>Chrome Extension</ListItemText>
                  <LaunchOutlined />
                </a>
              </ListItemButton>
              <ListItemButton onClick={() => extensionClickHandler('word')}>
                <a className={classes.productLink}>
                  <ListItemIcon>
                    <img src="/emojis/solid_msword.svg" alt="microsoft word extension" />
                  </ListItemIcon>
                  <ListItemText sx={{ marginRight: '8px' }}>Word Extension</ListItemText>
                  <LaunchOutlined />
                </a>
              </ListItemButton>
            </List>
            <Divider />
          </>
        )}
        <Divider />
        <List>
          <ListItemButton>
            <a
              className={classes.productLink}
              target="_blank"
              href="https://help.quillbot.com"
              rel="noreferrer"
            >
              <ListItemIcon>
                <HelpOutlineOutlined style={{ color: theme.specialColors.ICON_DEFAULT_COLOR }} />
              </ListItemIcon>
              <ListItemText>Help Center</ListItemText>
            </a>
          </ListItemButton>
          <ListItemButton>
            <SidebarLink href="/contact">
              <a className={`${classes.productLink}`} target={isExtension ? '_blank' : ''}>
                <div
                  className={`${pageUrl === '/contact' ? classes.active : ''}`}
                  style={{ borderColor: theme.palette.primary.main }}
                />
                <ListItemIcon>
                  <MailOutlined style={{ color: theme.specialColors.ICON_DEFAULT_COLOR }} />
                </ListItemIcon>
                <ListItemText className={`${pageUrl === '/contact' ? classes.boldText : ''}`}>
                  Contact Us
                </ListItemText>
              </a>
            </SidebarLink>
          </ListItemButton>
        </List>
        {(isMobile || isExtension) && (
          <>
            <Divider />
            <List style={{ padding: 0 }}>
              <ListItemButton
                style={{ padding: '12px 0' }}
                onClick={() => {
                  if (languageMenuOpen) {
                    setAnchorEl(null);
                  } else {
                    setAnchorEl(buttonRef.current);
                  }
                  setLanguageMenuOpen(!languageMenuOpen);
                }}
                ref={buttonRef}
              >
                <a className={classes.productLink}>
                  <ListItemIcon>
                    <img
                      src={props.LANGUAGES[regionalLanguage].imagePath}
                      alt="language menu"
                      className={classes.productIcon}
                      style={{
                        borderRadius: '50%',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText>{props.LANGUAGES[regionalLanguage].displayText}</ListItemText>
                  <ArrowDropDown
                    style={{
                      transform: `rotate(${languageMenuOpen ? 180 : 0}deg)`,
                    }}
                  />
                </a>
              </ListItemButton>
            </List>
            <Divider />
            {![
              '/co-writer',
              '/co-writer/home',
              '/citation-generator',
              '/extension-success',
              'exit-survey',
              '/guides/paraphrasing',
              '/guides/ai-writing-assistant',
              '/plagiarism-checker',
            ].includes(router.pathname) && (
              <List style={{ padding: 0 }}>
                <ListItemButton
                  style={{ paddingTop: 12, paddingBottom: 12 }}
                  onClick={() => props.handleThemeChange()}
                >
                  <a className={classes.productLink}>
                    <ListItemIcon>
                      {theme.palette.mode === 'light' ? (
                        <Brightness2
                          className={classes.productIcon}
                          style={{
                            transform: 'rotate(150deg)',
                            color: theme.specialColors.ICON_DEFAULT_COLOR,
                          }}
                        />
                      ) : (
                        <Brightness7
                          className={classes.productIcon}
                          style={{
                            color: theme.specialColors.ICON_DEFAULT_COLOR,
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText>
                      {theme.palette.mode === 'light'
                        ? 'Switch to Dark Mode'
                        : 'Switch to Light Mode'}
                    </ListItemText>
                  </a>
                </ListItemButton>
              </List>
            )}
            <LanguageMenu
              open={languageMenuOpen}
              setLanguageMenuOpen={setLanguageMenuOpen}
              handleLanguageChange={handleLanguageChange}
              LANGUAGES={props.LANGUAGES}
              anchorElement={anchorEl}
            />
          </>
        )}
      </div>
      <Box className={classes.appVersionBar}>{appVersion}</Box>
    </>
  );

  return (
    <>
      {!isMobile && !isExtension && !native ? (
        <StyledDrawer
          variant="permanent"
          className={`${classes.sidebar} ${classes.desktopSidebar} ${
            props.sidebarHoverOpen && classes.sidebarHovered
          }`}
          classes={{
            paper: classes.sidebarPaper,
          }}
          open={props.sidebarHoverOpen || (sidebarPinned && !isCoWriter)}
          onMouseOver={() => {
            props.setSidebarHoverOpen(true);
          }}
          onMouseLeave={() => {
            props.setSidebarHoverOpen(false);
          }}
        >
          {drawer}
        </StyledDrawer>
      ) : (
        <Drawer
          ModalProps={{
            onBackdropClick: () => {
              dispatch(setSidebarPinnned(false, 'user'));
              setAnchorEl(null);
              setLanguageMenuOpen(false);
            },
          }}
          anchor="right"
          className={`${classes.mobileSidebar} ${isExtension && classes.extensionSidebar}`}
          open={props.sidebarHoverOpen || (sidebarPinned && !isCoWriter)}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
